import type { AiCreditState, AiRemedy } from './credit-state';

/**
 * The ONLY source of AI degradation copy (Plan Phase 6-shared).
 *
 * ── Why a table and not inline strings ──────────────────────────────────────
 * 362 files in this app import `sonner` and there are 867 `toast.error(...)`
 * call sites. Every one of them was free to invent its own wording, and they
 * did. The ~40 AI surfaces this toolkit serves would have drifted the same way
 * within a week: "You're out of credits", "AI quota exceeded", "Limit reached"
 * — three sentences for one condition, none of them naming the remedy.
 *
 * So: every user-facing sentence about credits, degradation or permission
 * lives here, and every component reads from here. A surface that needs
 * different wording changes THIS file, which changes it everywhere at once.
 *
 * ── Hard rules, guarded by lib/ai/__tests__/quota-copy.spec.ts ──────────────
 *  1. No provider name, model name, endpoint, parameter or HTTP status in any
 *     string. The raw upstream text belongs in the collapsed technical
 *     disclosure (`lib/ai/errors.ts` keeps it on `AiError.technical`), never in
 *     the sentence a customer reads. A guard spec walks every exported string.
 *  2. No upgrade CTA. The billing page's Contact Sales button is inert, so
 *     "Upgrade your plan" would be a dead end. The owner dropped the
 *     `'upgrade'` remedy from the contract for exactly this reason.
 *  3. A remedy must be one the caller can actually perform. The SERVER filters
 *     `remedies[]` by ability (`manage:AiStudio` is admin-only), and this file
 *     just renders what arrives — see `remedyCopy`.
 *  4. English only. `apps/web` has no i18n layer and this is not the place to
 *     invent one (owner decision).
 */

export interface AiStateCopy {
  /** One plain sentence. This is what goes on top. */
  headline: string;
  /** One more sentence of context. Never technical. */
  body: string;
}

/**
 * The sentence for each credit state.
 *
 * `unknown` gets an honest sentence of its own. It must never borrow the
 * healthy wording — the whole reason the state exists is that a probe which
 * could not answer is not a tenant with credits to spare.
 */
export const AI_STATE_COPY: Record<AiCreditState, AiStateCopy> = {
  healthy: {
    headline: 'AI credits are available.',
    body: 'Generation is running on the credits included with your subscription.',
  },
  warning: {
    headline: 'AI credits are running low.',
    body: 'Generation still works, but the credits included for this month are close to their limit.',
  },
  exhausted: {
    headline: 'AI credits for this month are used up.',
    body: 'Generation is paused until the included credits reset, or until an AI provider key is added for this workspace.',
  },
  degraded: {
    headline: 'AI is running in reduced mode.',
    body: 'Requests are still being answered, but results may be shorter or less detailed than usual.',
  },
  unknown: {
    headline: 'AI credit status could not be checked.',
    body: 'The remaining credits for this month are not known right now. Generation will still be attempted, and the limit is applied by the server either way.',
  },
};

/** Falls back to the honest `unknown` sentence for an unrecognised literal. */
export function stateCopy(state: string): AiStateCopy {
  return AI_STATE_COPY[state as AiCreditState] ?? AI_STATE_COPY.unknown;
}

/**
 * The two full-surface gates. These are different situations and they get
 * different words:
 *
 *  - `exhausted`      — you may use this, there is nothing left to use.
 *  - `not-permitted`  — this is switched off for you; there is a person who
 *                       can change that, and it is not a purchase.
 *
 * Extracted from the inline `AiStudioDisabled` in
 * `app/phishing/ai-studio/StudioRoute.tsx`, which had only the second case and
 * would have grown a near-duplicate for the first.
 */
export interface AiGateCopy {
  title: string;
  body: string;
}

export const AI_GATE_COPY: Record<'exhausted' | 'not-permitted', AiGateCopy> = {
  exhausted: {
    title: 'AI credits for this month are used up',
    body: 'Generation is paused until the included credits reset at the start of next month. Adding an AI provider key for this workspace resumes it right away.',
  },
  'not-permitted': {
    title: "AI assistance isn't enabled for your account",
    body: 'Generating with AI is switched off here. Ask a workspace administrator to enable it, then reload this page.',
  },
};

export interface AiRemedyCopy {
  label: string;
  description: string;
  /** Null when the caller cannot reach the destination themselves. */
  href: string | null;
}

/**
 * The two remedies the server can send.
 *
 * `ask_admin` has a NULL href on purpose. `create:AiStudio` is a workspace
 * EDITOR grant, but `manage:AiStudio` — needed to add a key — is ADMIN-only.
 * Handing an editor a deep link into the key dialog sends them to a page that
 * will refuse them; the honest remedy is the name of the person who can do it.
 */
export const AI_REMEDY_COPY: Record<AiRemedy, AiRemedyCopy> = {
  add_own_key: {
    label: 'Add your own AI provider key',
    description:
      'Use a key you supply for this workspace instead of the included credits.',
    href: buildAddKeyUrl(),
  },
  ask_admin: {
    label: 'Ask a workspace administrator',
    description:
      'Adding an AI provider key needs administrator access, which this account does not have.',
    href: null,
  },
};

/**
 * Resolve a remedy, optionally deep-linking to a specific provider card.
 *
 * `ask_admin` never gains an href, whatever provider is passed — that is the
 * property that makes "a remedy must be one the caller can perform" hold by
 * construction rather than by review.
 */
export function remedyCopy(
  remedy: AiRemedy,
  provider?: string | null,
): AiRemedyCopy {
  const base = AI_REMEDY_COPY[remedy];
  if (remedy !== 'add_own_key') return base;
  return { ...base, href: buildAddKeyUrl(provider) };
}

/** Shown on any surface that produced a degraded result. */
export const AI_DEGRADED_COPY: AiStateCopy = {
  headline: 'This ran in reduced mode.',
  body: 'The result came back with lower-quality settings than were asked for, so it may be shorter or less detailed. Generate again later for a full-quality result.',
};

/** Shown when the ceiling is measured but not applied (observe mode). */
export const AI_OBSERVE_MODE_COPY: AiStateCopy = {
  headline: 'Credit limits are not being applied yet.',
  body: 'Usage is being measured so you can see where you stand, but nothing is blocked while this is on.',
};

/** Prefix for the reset date, e.g. "Credits reset on 1 September". */
export const AI_WINDOW_RESET_LABEL = 'Included credits reset on';

/** Reason string for a control disabled because the ceiling was reached. */
export const AI_DISABLED_REASON = {
  exhausted:
    'AI credits for this month are used up, so this action is unavailable right now.',
  notPermitted:
    'AI assistance is switched off for this account, so this action is unavailable.',
} as const;

/**
 * The deep link `/settings/ai-studio` has parsed since the day it shipped:
 *
 *   const highlightProvider = searchParams.get('highlight') ?? undefined;
 *   const openKeyDialog     = searchParams.get('open-key-dialog') === '1';
 *
 * …and which NOTHING in the repo ever constructed. Every quota banner that
 * wanted to send an admin to the key dialog either linked to the bare settings
 * page or hand-wrote the query string. This is the missing producer; it is the
 * only thing allowed to build that URL.
 *
 * The provider is a caller-supplied slug (never a hardcoded vendor name in
 * this file), url-encoded rather than spliced, and the result is always a
 * same-origin relative path so it is safe to hand to `next/link`.
 */
export function buildAddKeyUrl(provider?: string | null): string {
  const slug = typeof provider === 'string' ? provider.trim() : '';
  const params = new URLSearchParams();
  if (slug) params.set('highlight', slug);
  params.set('open-key-dialog', '1');
  // URLSearchParams encodes a space as '+'; the settings page reads the value
  // back through searchParams.get(), which decodes '%20' and '+' alike, but
  // '%20' is what a hand-written link would carry — keep the two identical so
  // the deep link is byte-comparable in a test.
  return `/settings/ai-studio?${params.toString().replace(/\+/g, '%20')}`;
}

/**
 * Copy for the BATCH PRE-CHECK (`lib/ai/batch-precheck.ts`).
 *
 * Two variants, because the figure may be withheld. Both say the same thing;
 * only one of them is allowed to name numbers:
 *
 *   - `withFigures` — the tenant may see its own ceiling, so the sentence is
 *     specific and actionable ("asks for 10, 3 left").
 *   - `withheld`    — an MSSP sub-tenant must not see its reseller's ceiling
 *     (`numberVisible: false`). The batch is still refused, and the reason
 *     still explains WHY, but it carries no digits at all. Note the withheld
 *     sentence is deliberately number-free rather than partially redacted: a
 *     sentence naming the request size but not the remainder lets a reader
 *     bracket the hidden figure across a couple of attempts.
 */
export const AI_BATCH_SHORTFALL_COPY = {
  headline: 'This run needs more AI credits than are left this month.',
  withheld:
    'Nothing was sent, because this run asks for more generations than the AI credits left this month allow. Ask for fewer at a time, or wait for the monthly reset.',
} as const;

/**
 * The specific, numbered reason. A function (so the guard spec skips it) that
 * composes ONLY from integers this app already holds — never from server text.
 */
export function batchShortfallReason(input: {
  requested: number;
  creditsRemaining: number;
  numberVisible: boolean;
}): string {
  if (!input.numberVisible) return AI_BATCH_SHORTFALL_COPY.withheld;

  const gens = input.requested === 1 ? 'generation' : 'generations';
  const credits = input.creditsRemaining === 1 ? 'credit' : 'credits';
  return (
    `Nothing was sent. This run asks for ${input.requested.toLocaleString('en-US')} ${gens}, ` +
    `but only ${input.creditsRemaining.toLocaleString('en-US')} AI ${credits} are left this month. ` +
    'Ask for fewer at a time, or wait for the monthly reset.'
  );
}

/**
 * Copy for a control whose SINGLE press spends once per unit of work.
 *
 * Content Studio shipped the counter-example: a hardcoded caption reading
 * "Optional · takes a few minutes · uses AI credit" beside a button that
 * generates one image per card. It said the same words for a 3-card deck and
 * a 40-card one, and the same words at 100,000 credits and at zero — so the
 * one number an author needed before pressing it (how many charges this press
 * is) was the one thing it never said.
 *
 * A function, so the copy-hygiene guard skips it: it composes only from an
 * integer this app already holds and two caller-supplied nouns, never from
 * server text.
 */
export function batchCostSentence(input: {
  units: number;
  /** Singular unit noun, e.g. 'card image'. */
  noun: string;
  /** Plural unit noun, e.g. 'card images'. */
  nounPlural: string;
}): string {
  const noun = input.units === 1 ? input.noun : input.nounPlural;
  return `Uses AI credits for ${input.units.toLocaleString('en-US')} ${noun} — one charge each.`;
}

/**
 * Shown when a surface completed WITHOUT AI and kept a non-AI result.
 *
 * The feedback widget already degraded gracefully — a failed `/feedback/summarize`
 * still files the ticket, carrying the reporter's own words as the summary.
 * What it did not do was say so, so a hand-written summary and a model-written
 * one were pixel-identical. Keeping the fallback and NAMING it is the whole
 * change; this is the sentence that names it.
 */
export const AI_NON_AI_FALLBACK_COPY: AiStateCopy = {
  headline: 'Written without AI assistance.',
  body: 'AI assistance was not available just now, so your report is being filed with your own words exactly as you wrote them. Nothing was lost — you can edit it below before submitting.',
};

/**
 * The credits figure as a sentence — or NULL when there is no figure to show.
 *
 * Two distinct reasons to return null, both of which must NOT be papered over
 * with a zero:
 *   - `numberVisible === false` — the figure is deliberately WITHHELD (an MSSP
 *     tenant is not shown its reseller's ceiling). The state still renders;
 *     only the number is suppressed.
 *   - `creditsRemaining === null` — we simply do not have it.
 *
 * A real `0` is a measurement and renders as "0", never as "no data".
 */
export function creditsSentence(input: {
  creditsRemaining: number | null;
  numberVisible: boolean;
}): string | null {
  if (!input.numberVisible) return null;
  if (input.creditsRemaining === null) return null;
  return `${input.creditsRemaining.toLocaleString('en-US')} AI credits left this month`;
}

/**
 * Copy for the case where the block is NOT the monthly credit allowance.
 *
 * Kept OUT of `AI_STATE_COPY` deliberately: that map is keyed by credit
 * STATE, and the daily output-token ceiling is a different limit on a
 * different clock. Folding it in would have meant either a fake state or a
 * key the type does not allow — and the bug being fixed here is precisely
 * that one string was doing duty for two ceilings.
 *
 * Never say "this month" in here. The block lifts at UTC midnight, and the
 * monthly credit figure shown beside it may be nowhere near spent — a tenant
 * at 3% of its credits saw "AI credits for this month are used up" on staging.
 */
export const AI_BLOCKER_COPY = {
  'daily-tokens': {
    headline: 'Today\u2019s AI limit has been reached.',
    body: 'Generation resumes at midnight UTC. This is a daily limit, so the AI credits included for this month are unaffected.',
  },
} as const;

/**
 * The copy for a blocked surface, given WHICH ceiling blocked.
 *
 * `stateCopy` alone cannot answer this: it is keyed by credit STATE, and the
 * daily output-token ceiling is a different limit on a different clock. One
 * string served both, so a tenant at 3% of its monthly credits was told to
 * come back next month when the block lifted at midnight.
 *
 * Falls back to the state copy whenever the server did not name a cause —
 * `null` means "we could not say which", and guessing "today" would be the
 * same error pointing the other way.
 */
export function blockedCopy(
  state: string,
  blockedBy: 'credits' | 'daily-tokens' | null | undefined,
): AiStateCopy {
  if (blockedBy === 'daily-tokens') return AI_BLOCKER_COPY['daily-tokens'];
  return stateCopy(state);
}
